import { useEffect, useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Employee } from '@lms/shared-types'
import { ApiError } from '../../api/client'
import {
  createReplacementGrants,
  type ReplacementGrant,
} from '../../api/replacement-grants'
import { CalendarMultiPicker } from '../primitives/CalendarMultiPicker'
import { DateField } from '../primitives/DateField'
import { EmployeeAndTeamPickerInput } from '../primitives/EmployeeAndTeamPickerInput'
import { Field } from '../primitives/Field'
import { useToast } from '../primitives/Toast'
import { useDayTypes } from '../../lib/use-day-types'

/** Mirrors the api fallback. The settings table is authoritative; this is a
 *  client-side default for the "Expires on" pre-fill only — the server
 *  re-derives expires_at when omitted. */
const DEFAULT_EXPIRY_DAYS = 90

interface FormState {
  selectedDates: Set<string>
  recipients: Employee[]
  notes: string
  notesTouched: boolean
  days: string
  daysTouched: boolean
  expiresAt: string
  expiresAtTouched: boolean
}

function initialState(): FormState {
  return {
    selectedDates: new Set(),
    recipients: [],
    notes: '',
    notesTouched: false,
    days: '',
    daysTouched: false,
    expiresAt: '',
    expiresAtTouched: false,
  }
}

function isoAddDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00`)
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

function isoToday(): string {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d.toISOString().slice(0, 10)
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return `${err.message} (${err.status})`
  if (err instanceof Error) return err.message
  return 'Unexpected error'
}

/** Min date in a set of ISO strings, or '' if empty. */
function minOf(dates: ReadonlySet<string>): string {
  if (dates.size === 0) return ''
  return Array.from(dates).reduce((a, b) => (a < b ? a : b))
}

/** Max date in a set of ISO strings, or '' if empty. */
function maxOf(dates: ReadonlySet<string>): string {
  if (dates.size === 0) return ''
  return Array.from(dates).reduce((a, b) => (a > b ? a : b))
}

const rowStyle: CSSProperties = { display: 'flex', gap: 16, flexWrap: 'wrap' }
const inputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: 13,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
  minHeight: 44,
  boxSizing: 'border-box',
  fontFamily: 'var(--font-sans)',
}
const textareaStyle: CSSProperties = {
  ...inputStyle,
  minHeight: 80,
  resize: 'vertical',
}
const submitStyle: CSSProperties = {
  padding: '10px 24px',
  borderRadius: 8,
  background: 'var(--steel-blue)',
  color: 'var(--surface)',
  fontSize: 14,
  fontWeight: 600,
  border: 'none',
  cursor: 'pointer',
  minHeight: 44,
  alignSelf: 'flex-start',
  fontFamily: 'var(--font-sans)',
}
const submitDisabledStyle: CSSProperties = {
  ...submitStyle,
  opacity: 0.5,
  cursor: 'not-allowed',
}
const aiHintStyle: CSSProperties = {
  fontSize: 11,
  color: 'var(--text-3)',
  margin: '4px 0 0',
}

interface Props {
  onCreated: (created: ReplacementGrant[]) => void
}

export function ReplacementGrantForm({ onCreated }: Readonly<Props>): JSX.Element {
  const [state, setState] = useState<FormState>(initialState)
  const { notify } = useToast()
  const queryClient = useQueryClient()

  // Derive from/to from the selected set so useDayTypes can classify day kinds.
  // When selectedDates is empty, both stay '' and the query stays disabled.
  const from = minOf(state.selectedDates)
  const to = maxOf(state.selectedDates)

  const dayTypesQuery = useDayTypes(from, to)

  // Only count day types for dates that are actually selected (the hook returns
  // all days in [from, to], which may include unselected days in between).
  const selectedDayTypes = useMemo(() => {
    if (!dayTypesQuery.data) return []
    return dayTypesQuery.data.filter((d) => state.selectedDates.has(d.date))
  }, [dayTypesQuery.data, state.selectedDates])

  const workedDates = useMemo(
    () => Array.from(state.selectedDates).sort(),
    [state.selectedDates],
  )

  const recipientIds = useMemo(
    () => state.recipients.map((e) => e.id),
    [state.recipients],
  )

  // Pre-fill days = count of selected dates when HR hasn't typed
  useEffect(() => {
    setState((prev) => {
      if (prev.daysTouched) return prev
      const n = prev.selectedDates.size
      const target = n > 0 ? n.toFixed(1) : ''
      if (prev.days === target) return prev
      return { ...prev, days: target }
    })
  }, [state.selectedDates.size])

  // Default expires_at = max(selectedDates) + 90 days, when HR hasn't typed
  useEffect(() => {
    if (state.expiresAtTouched) return
    const maxDate = maxOf(state.selectedDates)
    if (!maxDate) {
      setState((prev) => ({ ...prev, expiresAt: '' }))
      return
    }
    const expiry = isoAddDays(maxDate, DEFAULT_EXPIRY_DAYS)
    setState((prev) => ({ ...prev, expiresAt: expiry }))
  }, [state.selectedDates, state.expiresAtTouched])

  // Build holiday list for the calendar from dayTypes data.
  // NOTE (v1 limitation): useDayTypes only fetches data within [from, to] —
  // the window derived from the selected dates. For calendar months where no
  // dates are yet selected, holiday indicators won't appear until the HR
  // clicks at least one date in that month. This is acceptable for v1 because
  // the grant context (which worked days to include) is the priority use case.
  const holidaysForCalendar = useMemo(() => {
    if (!dayTypesQuery.data) return []
    return dayTypesQuery.data
      .filter((d) => d.kind === 'public_holiday' && d.holidayName)
      .map((d) => ({ date: d.date, name: d.holidayName! }))
  }, [dayTypesQuery.data])

  const summary = useMemo(() => {
    const ph = selectedDayTypes.filter((r) => r.kind === 'public_holiday').length
    const wknd = selectedDayTypes.filter(
      (r) => r.kind === 'saturday' || r.kind === 'sunday',
    ).length
    return { total: selectedDayTypes.length, ph, wknd }
  }, [selectedDayTypes])

  const mutation = useMutation({
    mutationFn: () => {
      const body: {
        recipientIds: string[]
        workedDates: string[]
        quantity: string
        notes?: string
        expiresAt?: string
      } = {
        recipientIds,
        workedDates,
        quantity: state.days,
      }
      const trimmed = state.notes.trim()
      if (trimmed) body.notes = trimmed
      if (state.expiresAt) body.expiresAt = state.expiresAt
      return createReplacementGrants(body)
    },
    onSuccess: (created) => {
      notify(
        `${created.length} grant${created.length === 1 ? '' : 's'} created`,
        'success',
      )
      void queryClient.invalidateQueries({ queryKey: ['replacement-grants'] })
      onCreated(created)
      setState(initialState)
    },
  })

  const canSubmit =
    state.selectedDates.size > 0 &&
    state.recipients.length > 0 &&
    state.days !== '' &&
    !mutation.isPending

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (canSubmit) mutation.mutate()
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
      aria-label="Create replacement grants"
    >
      {/* Worked dates — inline multi-select calendar */}
      <Field label="Worked dates" required>
        <div>
          <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 8, marginTop: 0 }}>
            Click any day to add it to the grant. Click again to remove.
          </p>
          <CalendarMultiPicker
            value={state.selectedDates}
            onChange={(next) => setState((p) => ({ ...p, selectedDates: next }))}
            holidays={holidaysForCalendar}
            max={isoToday()}
            data-testid="rg-worked-dates"
          />
          {state.selectedDates.size > 0 ? (
            <p style={{ ...aiHintStyle, marginTop: 8 }}>
              {summary.total} worked day{summary.total === 1 ? '' : 's'}
              {summary.ph > 0
                ? ` · ${summary.ph} public holiday${summary.ph === 1 ? '' : 's'}`
                : ''}
              {summary.wknd > 0
                ? ` · ${summary.wknd} weekend day${summary.wknd === 1 ? '' : 's'}`
                : ''}
            </p>
          ) : null}
        </div>
      </Field>

      {/* Recipients */}
      <Field label="Recipients" htmlFor="rg-recipients" required>
        <EmployeeAndTeamPickerInput
          testId="rg-recipients"
          value={state.recipients}
          onChange={(next) => setState((p) => ({ ...p, recipients: next }))}
          maxRecipients={50}
        />
      </Field>

      {/* Notes (AI-drafted) */}
      <div>
        <Field label="Notes" htmlFor="rg-notes">
          <textarea
            id="rg-notes"
            value={state.notes}
            onChange={(e) =>
              setState((p) => ({
                ...p,
                notes: e.target.value,
                notesTouched: true,
              }))
            }
            maxLength={1000}
            style={textareaStyle}
            placeholder="Optional context"
          />
        </Field>
      </div>

      {/* Days + expiry row */}
      <div style={rowStyle}>
        <div style={{ width: 160 }}>
          <Field label="Days granted" htmlFor="rg-days" required>
            <input
              id="rg-days"
              type="number"
              step={0.5}
              min={0.5}
              max={5}
              value={state.days}
              onChange={(e) =>
                setState((p) => ({
                  ...p,
                  days: e.target.value,
                  daysTouched: true,
                }))
              }
              style={inputStyle}
              required
            />
          </Field>
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <Field label="Expires on" htmlFor="rg-expiry">
            <DateField
              value={state.expiresAt || null}
              min={
                to ? isoAddDays(to, 1) : undefined
              }
              onChange={(iso) =>
                setState((p) => ({
                  ...p,
                  expiresAt: iso ?? '',
                  expiresAtTouched: true,
                }))
              }
            />
          </Field>
        </div>
      </div>

      {mutation.isError ? (
        <p
          role="alert"
          style={{ color: 'var(--danger)', fontSize: 13, margin: 0 }}
        >
          {errorMessage(mutation.error)}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={!canSubmit}
        style={canSubmit ? submitStyle : submitDisabledStyle}
        aria-busy={mutation.isPending}
      >
        {mutation.isPending
          ? 'Creating…'
          : `Create grant${state.recipients.length === 1 || state.recipients.length === 0 ? '' : `s (${state.recipients.length})`}`}
      </button>
    </form>
  )
}
